import "./index.css";
import { Route, Switch, Redirect } from "wouter";
import { SignedIn, SignedOut, useAuth } from "@clerk/clerk-react";
import { Landing } from "./pages/Landing";
import { Login, Signup } from "./pages/Auth";
import { DashboardOverview } from "./pages/Dashboard";
import { APIKeysPage } from "./pages/APIKeys";
import { UsagePage } from "./pages/Usage";
import { BillingPage } from "./pages/Billing";
import { SettingsPage } from "./pages/Settings";
import { PricingPage } from "./pages/Pricing";
import { DocsPage } from "./pages/Docs";
import { CloudRain } from "lucide-react";

// Protected route wrapper - redirects to login if not signed in
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <>
      <SignedIn>{children}</SignedIn>
      <SignedOut>
        <Redirect to="/login" />
      </SignedOut>
    </>
  );
}

// Auth route wrapper - redirects to dashboard if already signed in
function AuthRoute({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();

  // Wait for Clerk to load before making redirect decisions
  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-100 via-white to-sky-50">
        <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // If signed in, redirect to dashboard
  if (isSignedIn) {
    return <Redirect to="/dashboard" />;
  }

  // Show auth page
  return <>{children}</>;
}

export function App() {
  return (
    <Switch>
      {/* Public pages */}
      <Route path="/" component={Landing} />
      <Route path="/pricing" component={PricingPage} />

      {/* Auth pages - redirect to dashboard if already signed in */}
      <Route path="/login">
        <AuthRoute>
          <Login />
        </AuthRoute>
      </Route>
      <Route path="/login/sso-callback">
        <AuthRoute>
          <Login />
        </AuthRoute>
      </Route>
      <Route path="/signup">
        <AuthRoute>
          <Signup />
        </AuthRoute>
      </Route>
      <Route path="/signup/sso-callback">
        <AuthRoute>
          <Signup />
        </AuthRoute>
      </Route>

      {/* Documentation (public) */}
      <Route path="/docs" component={DocsPage} />
      <Route path="/docs/:slug" component={DocsPage} />

      {/* Dashboard pages - protected */}
      <Route path="/dashboard">
        <ProtectedRoute>
          <DashboardOverview />
        </ProtectedRoute>
      </Route>
      <Route path="/dashboard/keys">
        <ProtectedRoute>
          <APIKeysPage />
        </ProtectedRoute>
      </Route>
      <Route path="/dashboard/usage">
        <ProtectedRoute>
          <UsagePage />
        </ProtectedRoute>
      </Route>
      <Route path="/dashboard/billing">
        <ProtectedRoute>
          <BillingPage />
        </ProtectedRoute>
      </Route>
      <Route path="/dashboard/settings">
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      </Route>

      {/* 404 fallback */}
      <Route>
        <div className="min-h-screen flex items-center justify-center bg-slate-50">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <CloudRain className="w-20 h-20 text-sky-500" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800 mb-2">
              404 - Page Not Found
            </h1>
            <p className="text-slate-500 mb-6">
              The page you're looking for doesn't exist.
            </p>
            <a
              href="/"
              className="px-6 py-3 bg-gradient-to-r from-sky-500 to-sky-600 text-white font-medium rounded-xl hover:opacity-90 transition-opacity"
            >
              Go Home
            </a>
          </div>
        </div>
      </Route>
    </Switch>
  );
}

export default App;
